import axios from 'axios';
import '../config';
import { ADVERTISER_STATUSES, OFFER_STATUSES, PARTNERSHIPS, fieldsFor, konkrPayload, validateKonkr } from '../utils/konkr';

const kinds = ['advertisers', 'offers'];
const actions = { advertisers: ['activate', 'deactivate'], offers: ['ready', 'publish', 'disable'] };
const validId = id => Number.isSafeInteger(id) && id > 0;
const endpoint = (kind, id, action) => {
  if (!kinds.includes(kind) || (id !== undefined && !validId(id))) throw new Error('A valid Konkr record is required.');
  return `${global.config.url}/support/konkr/${kind}${id === undefined ? '' : `/${id}`}${action ? `/${action}` : ''}`;
};
const config = params => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, ...(params ? { params } : {}) });
const requireRecord = (kind, data, id) => {
  const statuses = kind === 'advertisers' ? ADVERTISER_STATUSES : OFFER_STATUSES;
  if (!data || !validId(data.id) || (id !== undefined && data.id !== id) ||
      !Number.isSafeInteger(data.revision) || data.revision < 0 || !statuses.includes(data.status) ||
      fieldsFor(kind).some(([key]) => data[key] !== null && data[key] !== undefined && typeof data[key] !== 'string') ||
      (kind === 'advertisers' ? typeof data.name !== 'string' || !PARTNERSHIPS.includes(data.awinPartnershipStatus) :
        !validId(data.advertiserId) || typeof data.isLive !== 'boolean' || typeof data.featured !== 'boolean' ||
        !Number.isSafeInteger(data.pointsRequired) || data.pointsRequired < 0)) {
    throw new Error('Unexpected Konkr response. Expected camelCase record fields, string statuses and an actual revision. Reload before repeating a change.');
  }
  return data;
};
const write = async (kind, id, operation, request, expectedStatus) => {
  const url = endpoint(kind, id, operation === 'update' || operation === 'create' ? undefined : operation);
  const response = operation === 'update' ? await axios.put(url, request, config()) : await axios.post(url, request, config());
  try {
    const record = requireRecord(kind, response.data, id);
    if (expectedStatus && record.status !== expectedStatus) throw new Error('The server did not confirm the requested Konkr lifecycle state.');
    return record;
  } catch (error) {
    error.code = 'KONKR_RESPONSE_UNVERIFIED';
    throw error;
  }
};
const requireRevision = revision => {
  if (!Number.isSafeInteger(revision) || revision < 0) throw new Error('Reload the actual record revision before saving.');
};
const save = (kind, form, record, draft = false) => {
  if (Object.keys(validateKonkr(kind, form)).length) throw new Error('Correct the Konkr form validation errors before saving.');
  if (record) requireRevision(record.revision);
  if (draft && (kind !== 'offers' || !record)) throw new Error('Save Draft requires an existing offer.');
  return write(kind, record ? record.id : undefined, draft ? 'draft' : record ? 'update' : 'create',
    konkrPayload(kind, form, record), !record ? kind === 'offers' ? 'Draft' : 'Inactive' : draft ? 'Draft' : undefined);
};
const konkrApi = {
  list: async (kind, filters = {}) => {
    const allowed = kind === 'advertisers' ? ['search', 'status', 'partnershipStatus'] : ['advertiserId', 'status'];
    const params = Object.fromEntries(allowed.filter(key => filters[key] !== '' && filters[key] !== null && filters[key] !== undefined).map(key => [key, filters[key]]));
    const { data } = await axios.get(endpoint(kind), config(params));
    if (!Array.isArray(data)) throw new Error('Unexpected Konkr list response. Expected an array; no mock data is substituted.');
    return data.map(record => requireRecord(kind, record));
  },
  get: async (kind, id) => requireRecord(kind, (await axios.get(endpoint(kind, id), config())).data, id),
  save,
  action: async (kind, id, action, revision) => {
    if (!actions[kind] || !actions[kind].includes(action)) throw new Error('Unsupported Konkr lifecycle action.');
    requireRevision(revision);
    const status = { activate: 'Active', deactivate: 'Inactive', ready: 'Ready', publish: 'Published', disable: 'Disabled' }[action];
    return write(kind, id, action, { revision }, status);
  },
};
export default konkrApi;
